import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useParams, Link } from 'react-router-dom';
import { 
  FileText, Calendar, Tag, ChevronRight, DollarSign, 
  Clock, FileType, AlertCircle, Loader2, Home
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Document type badge colors
const DOC_TYPE_COLORS = {
  'Insurance Policy': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  'Training Brochure': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  'Real Estate Document': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  'Legal Contract': 'bg-red-500/20 text-red-400 border-red-500/30',
  'Financial Document': 'bg-green-500/20 text-green-400 border-green-500/30',
  'Marketing Material': 'bg-pink-500/20 text-pink-400 border-pink-500/30',
  'Technical Documentation': 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  'General Document': 'bg-slate-500/20 text-slate-400 border-slate-500/30',
};

const PublicDocView = () => {
  const { token } = useParams();
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDocument();
  }, [token]);

  const fetchDocument = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BACKEND_URL}/api/documents/public/${token}`);
      setDocument(res.data);
    } catch (err) {
      console.error('Failed to fetch document:', err);
      if (err.response?.status === 404) {
        setError('Document not found or link is invalid');
      } else if (err.response?.status === 410) {
        setError('This link has expired');
      } else {
        setError('Failed to load document');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-emerald-400 animate-spin mx-auto mb-4" />
          <p className="text-emerald-300/70">Loading document...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-6">
        <Card className="bg-[#052e22]/50 border-emerald-800/30 max-w-md w-full">
          <CardContent className="py-12 text-center">
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Document Unavailable</h2>
            <p className="text-emerald-300/70 mb-6">{error}</p>
            <Link to="/">
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <Home className="w-4 h-4 mr-2" />
                Go to Home
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const analysis = document?.analysis;

  return (
    <div data-testid="public-doc-view" className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Header */}
      <header className="bg-[#0a5c44] py-4 px-6">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText className="w-8 h-8 text-white" />
            <div>
              <h1 className="text-lg font-bold text-white">AI Lead Mantra</h1>
              <p className="text-xs text-emerald-200/70">Document Analysis</p>
            </div>
          </div>
          <Badge className="bg-white/20 text-white border-white/30">
            Shared Document
          </Badge>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-5xl mx-auto p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* Document Header */}
          <Card className="bg-[#052e22]/50 border-emerald-800/30">
            <CardContent className="p-6">
              <Badge className={`mb-3 ${DOC_TYPE_COLORS[analysis?.document_type] || DOC_TYPE_COLORS['General Document']}`}>
                {analysis?.document_type || 'Document'}
              </Badge>
              <h2 className="text-2xl font-bold text-white mb-2">
                {analysis?.title || document?.filename}
              </h2>
              <p className="text-emerald-300/80 leading-relaxed">
                {analysis?.summary}
              </p>
              <div className="flex items-center gap-4 mt-4 pt-4 border-t border-emerald-800/30 text-sm text-emerald-300/70">
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {formatDate(document?.created_at)}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {document?.public_view_count || 1} views
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Key Points */}
          {analysis?.key_points?.length > 0 && (
            <Card className="bg-[#052e22]/50 border-emerald-800/30">
              <CardHeader className="border-b border-emerald-800/30 pb-4">
                <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                  <Tag className="w-5 h-5 text-emerald-400" />
                  Key Points
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <ul className="space-y-3">
                  {analysis.key_points.map((point, index) => (
                    <motion.li
                      key={index}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="flex items-start gap-3 text-emerald-100"
                    >
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center text-xs font-medium text-emerald-400">
                        {index + 1}
                      </span>
                      <span className="leading-relaxed">{point}</span>
                    </motion.li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Additional Info Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Dates */}
            {analysis?.dates?.length > 0 && (
              <Card className="bg-[#052e22]/50 border-emerald-800/30">
                <CardHeader className="border-b border-emerald-800/30 pb-4">
                  <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-emerald-400" />
                    Important Dates
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <ul className="space-y-2">
                    {analysis.dates.map((date, index) => (
                      <li key={index} className="flex items-center gap-2 text-emerald-200 text-sm">
                        <ChevronRight className="w-4 h-4 text-emerald-500" />
                        {date}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Pricing */}
            {analysis?.pricing?.length > 0 && (
              <Card className="bg-[#052e22]/50 border-emerald-800/30">
                <CardHeader className="border-b border-emerald-800/30 pb-4">
                  <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-emerald-400" />
                    Pricing Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <ul className="space-y-2">
                    {analysis.pricing.map((price, index) => (
                      <li key={index} className="flex items-center gap-2 text-emerald-200 text-sm">
                        <ChevronRight className="w-4 h-4 text-emerald-500" />
                        {price}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Key Terms */}
          {analysis?.key_terms?.length > 0 && (
            <Card className="bg-[#052e22]/50 border-emerald-800/30">
              <CardHeader className="border-b border-emerald-800/30 pb-4">
                <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                  <FileType className="w-5 h-5 text-emerald-400" />
                  Key Terms & Conditions
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="flex flex-wrap gap-2">
                  {analysis.key_terms.map((term, index) => (
                    <Badge
                      key={index}
                      className="bg-emerald-900/50 text-emerald-300 border border-emerald-800/50"
                    >
                      {term}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Footer CTA */}
          <Card className="bg-[#0a5c44] border-emerald-700/50">
            <CardContent className="py-6 text-center">
              <h3 className="text-xl font-bold text-white mb-2">
                Want to analyze your own documents?
              </h3>
              <p className="text-emerald-200/80 mb-4">
                AI Lead Mantra helps you extract insights from any document in seconds.
              </p>
              <Link to="/">
                <Button className="bg-white text-[#0a5c44] hover:bg-emerald-50 font-semibold">
                  Try AI Lead Mantra Free
                </Button>
              </Link>
            </CardContent>
          </Card>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="py-6 text-center">
        <p className="text-emerald-300/50 text-sm">
          Powered by AI Lead Mantra • aileadmantra.com
        </p>
      </footer>
    </div>
  );
};

export default PublicDocView;
